import React, { useEffect, useState } from "react";
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Modal,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { useGetReleaseNotes } from "services/input-core/release-notes/get-release-notes";
import { ReleaseNote } from "services/input-core/release-notes/@types";
import { useLanguageSettings } from "store/user-settings/user-settings-store";
import { useSubscription } from "store/auth/auth-store";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Divider } from "@material-ui/core";
import { Circle } from "@mui/icons-material";
import { useSendReleaseNoteViewed } from "services/input-core/release-notes/release-note-registry-view";

const NotificationPopup: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseNote | null>(
    null,
  );
  const { language } = useLanguageSettings();
  const { subscription } = useSubscription();
  const subscriptionLocalStorage = localStorage.getItem("subscription");

  const { t } = useTranslation("documents");

  const { data, isLoading, refetch } = useGetReleaseNotes(
    {
      languageType: language === "it" ? "ITA" : language?.toUpperCase(),
      subscription: subscription?.subscriptionId ?? subscriptionLocalStorage,
    },
    {
      enabled: !!(language && (subscription || subscriptionLocalStorage)),
    },
  );

  const { mutate: markAsViewed } = useSendReleaseNoteViewed();

  useEffect(() => {
    if (subscription?.subscriptionId) {
      refetch();
    }
  }, [subscription, language]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenModal = (release: ReleaseNote) => {
    setSelectedRelease(release);
    handleClose();

    markAsViewed(
      { releaseNoteId: release._id },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const handleCloseModal = () => {
    setSelectedRelease(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  return (
    <div>
      <IconButton aria-describedby={id} color="inherit" onClick={handleClick}>
        <Badge badgeContent={data?.unViewed} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          ".MuiPaper-root": {
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          },
        }}
      >
        <Box sx={{ width: 400 }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              px: 2,
              py: 1,
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h6">{t("notifications")}</Typography>
          </Box>

          {isLoading && <Skeleton style={{ padding: 2 }} height={200} />}

          {data?.releaseNotes === undefined && !isLoading && (
            <Typography sx={{ px: 2, py: 2 }}>
              {t("noNotifications")}
            </Typography>
          )}

          <List
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
            }}
          >
            {data?.releaseNotes?.map((note: ReleaseNote) => (
              <ListItem
                key={note._id}
                divider
                button
                onClick={() => handleOpenModal(note)}
                sx={{
                  ":hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <ListItemText
                  style={{ padding: "10px" }}
                  primary={`${t("Release")} - ${new Date(
                    note.date,
                  ).toLocaleDateString("pt-br", {
                    timeZone: "UTC",
                  })}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      <Modal
        open={!!selectedRelease}
        onClose={handleCloseModal}
        aria-labelledby="release-modal-title"
        aria-describedby="release-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 2,
            width: "60vw",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
            }}
          >
            <Typography id="release-modal-title" variant="h6" component="h2">
              {`${t("Release")}  ${new Date(
                selectedRelease?.date,
              ).toLocaleDateString("pt-br", {
                timeZone: "UTC",
              })}`}
            </Typography>
            <IconButton
              sx={{ position: "absolute", top: -15, right: -10 }}
              onClick={handleCloseModal}
            >
              <CloseIcon />
            </IconButton>
            <Divider style={{ backgroundColor: "#808080", margin: "10px 0" }} />
          </Box>

          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              marginTop: "20px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {selectedRelease?.fix.length > 0 && <b>{t("Fixes")}</b>}
              <List>
                {selectedRelease?.fix?.map((fix) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "start",
                      flexDirection: "column",
                      justifyContent: "start",
                      marginTop: "5px",
                      padding: "0 10px",
                    }}
                    key={fix.objId}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      <Circle sx={{ fontSize: "6px", marginRight: "5px" }} />

                      {fix?.languageTexts[0]?.title}
                    </span>
                    <span style={{ margin: "0 10px" }}>
                      {fix?.languageTexts[0]?.description}
                    </span>
                  </div>
                ))}
              </List>

              {selectedRelease?.feature?.length > 0 && <b>{t("Features")}:</b>}
              <List>
                {selectedRelease?.feature?.map((feature) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "start",
                      flexDirection: "column",
                      justifyContent: "start",
                      marginTop: "5px",
                      padding: "0 10px",
                    }}
                    key={feature.objId}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      <Circle sx={{ fontSize: "6px", marginRight: "5px" }} />
                      {feature?.languageTexts[0]?.title}
                    </span>
                    <span style={{ margin: "0 10px" }}>
                      {feature?.languageTexts[0]?.description}
                    </span>
                  </div>
                ))}
              </List>
            </Typography>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default NotificationPopup;
